import { useCallback, useEffect, useRef, useState } from 'react';
import { apiModules } from '../../api/apiService';
import { Recipe } from '../../api/Recipe';
import {
  getRecipeSortKey,
  isDefaultRecipeSort,
  mergeRecipePages,
  recipeMatchesFilters,
  RECIPES_PER_PAGE,
} from './recipeListQuery.helpers';

interface UseRecipeListQueryOptions {
  selectedTagIds: number[];
  selectedAuthorIds: number[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
}

interface UseRecipeListQueryResult {
  allRecipes: Recipe[];
  dailySelectionRecipe: Recipe | null;
  setAllRecipes: React.Dispatch<React.SetStateAction<Recipe[]>>;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isRefreshing: boolean;
  loadMore: () => void;
  refreshRecipes: () => Promise<void>;
}

export const useRecipeListQuery = ({
  selectedTagIds,
  selectedAuthorIds,
  sortField,
  sortDirection,
}: UseRecipeListQueryOptions): UseRecipeListQueryResult => {
  const [allRecipes, setAllRecipes] = useState<Recipe[]>([]);
  const [dailySelectionRecipe, setDailySelectionRecipe] = useState<Recipe | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const fetchIdRef = useRef(0);

  const loadRecipes = useCallback(async (
    pageIndex: number,
    isNewFilter: boolean = false,
    refresh: boolean = false
  ) => {
    const currentFetchId = ++fetchIdRef.current;

    if (pageIndex === 0 && !refresh) {
      setLoading(true);
    }

    if (pageIndex > 0) {
      setLoadingMore(true);
    }

    try {
      const recipes = await apiModules.recipes.getAllRecipes(
        pageIndex * RECIPES_PER_PAGE,
        RECIPES_PER_PAGE,
        selectedTagIds,
        selectedAuthorIds,
        getRecipeSortKey(sortField, sortDirection)
      );

      if (currentFetchId !== fetchIdRef.current) {
        return;
      }

      if (isNewFilter || refresh) {
        setAllRecipes(recipes);
        setPage(0);
      } else {
        setAllRecipes((previousRecipes) => mergeRecipePages(previousRecipes, recipes));
        setPage(pageIndex);
      }

      setHasMore(recipes.length === RECIPES_PER_PAGE);
    } catch (error) {
      console.error('Erreur lors du chargement des recettes:', error);
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
        setLoadingMore(false);

        if (refresh) {
          setIsRefreshing(false);
        }
      }
    }
  }, [selectedAuthorIds, selectedTagIds, sortDirection, sortField]);

  useEffect(() => {
    void loadRecipes(0, true);
  }, [loadRecipes]);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && !loading) {
      const nextPage = page + 1;
      void loadRecipes(nextPage, false);
    }
  }, [hasMore, loadRecipes, loading, loadingMore, page]);

  const refreshRecipes = useCallback(async () => {
    setIsRefreshing(true);
    await loadRecipes(0, true, true);
  }, [loadRecipes]);

  const loadDailySelectionRecipe = useCallback(async () => {
    if (!isDefaultRecipeSort(sortField, sortDirection)) {
      setDailySelectionRecipe(null);
      return;
    }

    try {
      const recipe = await apiModules.recipes.getDailySelectionRecipe();
      setDailySelectionRecipe(
        recipe && recipeMatchesFilters(recipe, selectedTagIds, selectedAuthorIds)
          ? recipe
          : null
      );
    } catch (error) {
      console.error('Erreur lors du chargement de la selection du jour:', error);
      setDailySelectionRecipe(null);
    }
  }, [selectedAuthorIds, selectedTagIds, sortDirection, sortField]);

  useEffect(() => {
    void loadDailySelectionRecipe();
  }, [loadDailySelectionRecipe]);

  return {
    allRecipes,
    dailySelectionRecipe,
    setAllRecipes,
    loading,
    loadingMore,
    hasMore,
    isRefreshing,
    loadMore,
    refreshRecipes,
  };
};

export default useRecipeListQuery;
